import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ClientUpdateNotificationSnackbar } from '../../../../components/organisms/ClientUpdateNotificationSnackbar'
import { useClientUpdateNotificationsQuery } from '../queries/useClientUpdateNotificationsQuery'

const SHOWN_NOTIFICATIONS_KEY = 'shownClientUpdateNotifications'

/**
 * クライアント更新通知 hook（TanStack Query版）
 *
 * メインページでのみ通知を取得し、未表示のものを Snackbar で表示する。
 * React Compiler対応: useEffectでのsetStateではなく、派生状態パターンを使用。
 */
export const useClientUpdateNotification = () => {
  const location = useLocation()
  const isMainPage = location.pathname === '/'

  const { data: notifications = [] } =
    useClientUpdateNotificationsQuery(isMainPage)

  // セッション内で閉じた通知のIDを追跡（即座のUI更新のため）
  const [dismissedIds, setDismissedIds] = useState<string[]>([])

  // 未表示通知のフィルタリングと優先度ソート
  // localStorageとセッション内dismissedの両方を考慮
  const availableNotifications = useMemo(() => {
    const shownIds = getShownNotificationIds()
    return notifications
      .filter(
        (notification) =>
          !shownIds.includes(notification.id) &&
          !dismissedIds.includes(notification.id)
      )
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 }
        return priorityOrder[b.priority] - priorityOrder[a.priority]
      })
  }, [notifications, dismissedIds])

  // 派生状態: メインページかつ利用可能な通知があれば最優先のものを表示
  const currentNotification = useMemo(() => {
    if (!isMainPage) return null
    return availableNotifications[0] || null
  }, [isMainPage, availableNotifications])

  const markNotificationAsShown = useCallback((notificationId: string) => {
    const shownIds = getShownNotificationIds()
    const updatedIds = [...shownIds, notificationId]
    localStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(updatedIds))
  }, [])

  const handleNotificationClose = useCallback(() => {
    if (currentNotification) {
      markNotificationAsShown(currentNotification.id)
      // 即座のUI更新のため、セッション内dismissedリストに追加
      setDismissedIds((prev) => [...prev, currentNotification.id])
    }
  }, [currentNotification, markNotificationAsShown])

  const renderNotification = () => {
    // メインページ（/）以外では通知を表示しない
    if (!isMainPage) return null

    // 通知の存在をチェック
    if (!currentNotification) return null

    return (
      <ClientUpdateNotificationSnackbar
        notification={currentNotification}
        onClose={handleNotificationClose}
      />
    )
  }

  return { renderNotification }
}

const getShownNotificationIds = (): string[] => {
  const stored = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY)
  return stored ? JSON.parse(stored) : []
}
